import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const ServicesPage = () => {
  const [expandedService, setExpandedService] = useState<number | null>(null);

  const services = [
    {
      icon: 'ri-file-list-3-line',
      title: 'Comprehensive Psychiatric Evaluations',
      shortDesc: 'Thorough initial assessments to understand your mental health needs',
      fullDesc: 'Our comprehensive psychiatric evaluations provide a complete assessment of your mental health status, medical history, and current symptoms. During this initial consultation, our experienced clinicians take the time to understand your unique situation, concerns, and goals for treatment.',
      treatments: [
        'Detailed clinical interview and history taking',
        'Mental status examination',
        'Diagnostic assessment using DSM-10 criteria',
        'Treatment plan development',
        'Medication recommendations when appropriate',
        'Coordination with other healthcare providers',
      ],
    },
    {
      icon: 'ri-medicine-bottle-line',
      title: 'Medication Management',
      shortDesc: 'Expert oversight and optimization of psychiatric medications',
      fullDesc: 'Our medication management services ensure that your psychiatric medications are working effectively with minimal side effects. We provide ongoing monitoring, dosage adjustments, and patient education to help you achieve the best possible outcomes.',
      treatments: [
        'Regular medication reviews and adjustments',
        'Side effect monitoring and management',
        'Drug interaction screening',
        'Patient education about medications',
        'Coordination with pharmacies and other providers',
        'Long-term maintenance and optimization',
      ],
    },
    {
      icon: 'ri-emotion-unhappy-line',
      title: 'Anxiety Disorders Treatment',
      shortDesc: 'Specialized care for various anxiety conditions',
      fullDesc: 'We provide comprehensive treatment for all types of anxiety disorders, using evidence-based approaches that combine medication management with supportive therapy and lifestyle interventions.',
      treatments: [
        'Generalized Anxiety Disorder (GAD)',
        'Panic Disorder and Panic Attacks',
        'Social Anxiety Disorder',
        'Specific Phobias',
        'Obsessive-Compulsive Disorder (OCD)',
        'Medication and therapy coordination',
      ],
    },
    {
      icon: 'ri-heart-pulse-line',
      title: 'Depression & Mood Disorders',
      shortDesc: 'Compassionate treatment for depression and related conditions',
      fullDesc: 'Our approach to treating depression and mood disorders is comprehensive and personalized, addressing both the biological and psychological aspects of these conditions to help you regain joy and function in your daily life.',
      treatments: [
        'Major Depressive Disorder',
        'Persistent Depressive Disorder (Dysthymia)',
        'Seasonal Affective Disorder',
        'Postpartum Depression',
        'Treatment-resistant depression options',
        'Ongoing monitoring and support',
      ],
    },
    {
      icon: 'ri-contrast-2-line',
      title: 'Bipolar Disorder Care',
      shortDesc: 'Specialized management of bipolar spectrum disorders',
      fullDesc: 'Managing bipolar disorder requires expertise and careful monitoring. Our clinicians specialize in helping patients achieve mood stability through medication management, lifestyle modifications, and ongoing support.',
      treatments: [
        'Bipolar I and Bipolar II Disorder',
        'Cyclothymic Disorder',
        'Mood stabilizer management',
        'Manic and depressive episode prevention',
        'Crisis intervention planning',
        'Family education and support',
      ],
    },
    {
      icon: 'ri-shield-cross-line',
      title: 'PTSD & Trauma Therapy',
      shortDesc: 'Trauma-informed care for post-traumatic stress',
      fullDesc: 'We provide compassionate, trauma-informed care for individuals dealing with PTSD and other trauma-related conditions. Our approach focuses on safety, stabilization, and gradual healing.',
      treatments: [
        'Post-Traumatic Stress Disorder (PTSD)',
        'Complex PTSD',
        'Acute Stress Disorder',
        'Trauma-focused medication management',
        'Coordination with trauma therapists',
        'Supportive crisis intervention',
      ],
    },
    {
      icon: 'ri-brain-line',
      title: 'Schizophrenia & Psychotic Disorders',
      shortDesc: 'Expert care for psychotic spectrum conditions',
      fullDesc: 'Our team has extensive experience in managing schizophrenia and other psychotic disorders. We provide comprehensive medication management and support to help patients maintain stability and quality of life.',
      treatments: [
        'Schizophrenia',
        'Schizoaffective Disorder',
        'Brief Psychotic Disorder',
        'Delusional Disorder',
        'Antipsychotic medication management',
        'Coordination with case management services',
      ],
    },
    {
      icon: 'ri-user-search-line',
      title: 'Dementia & Cognitive Care',
      shortDesc: 'Specialized support for cognitive disorders in seniors',
      fullDesc: 'We provide compassionate care for geriatric patients experiencing dementia and other cognitive disorders, focusing on maintaining quality of life and managing behavioral symptoms.',
      treatments: [
        'Alzheimer\'s Disease',
        'Vascular Dementia',
        'Lewy Body Dementia',
        'Behavioral symptom management',
        'Caregiver education and support',
        'Coordination with memory care facilities',
      ],
    },
    {
      icon: 'ri-parent-line',
      title: 'Neurodevelopmental Disorders',
      shortDesc: 'Adult ADHD and autism spectrum support',
      fullDesc: 'We provide specialized care for adults with neurodevelopmental disorders, helping them manage symptoms and improve daily functioning through medication management and supportive interventions.',
      treatments: [
        'Adult ADHD/ADD',
        'Autism Spectrum Disorder in adults',
        'Stimulant and non-stimulant medication management',
        'Executive function support',
        'Workplace accommodation guidance',
        'Ongoing monitoring and adjustments',
      ],
    },
    {
      icon: 'ri-flask-line',
      title: 'Substance Use Disorder Treatment',
      shortDesc: 'Integrated care for co-occurring substance use issues',
      fullDesc: 'We provide integrated treatment for individuals dealing with both mental health conditions and substance use disorders, addressing both issues simultaneously for better outcomes.',
      treatments: [
        'Dual diagnosis treatment',
        'Medication-assisted treatment (MAT)',
        'Alcohol use disorder management',
        'Opioid use disorder treatment',
        'Coordination with addiction specialists',
        'Relapse prevention support',
      ],
    },
    {
      icon: 'ri-computer-line',
      title: 'Telepsychiatry Services',
      shortDesc: 'Secure virtual mental health care from anywhere',
      fullDesc: 'Our telepsychiatry services provide the same high-quality care as in-office visits, delivered through secure video conferencing. This option offers convenience and accessibility for patients who prefer remote care.',
      treatments: [
        'HIPAA-compliant video sessions',
        'Same comprehensive evaluations',
        'Medication management via telehealth',
        'Flexible scheduling options',
        'Electronic prescriptions',
        'Secure messaging with providers',
      ],
    },
    {
      icon: 'ri-home-heart-line',
      title: 'Concierge Services',
      shortDesc: 'In-home psychiatric care for those who need it (upcharge applies)',
      fullDesc: 'For patients who have difficulty traveling to our office, we offer concierge services. Our clinicians can provide comprehensive psychiatric care in the comfort and safety of your own home. Please note that an upcharge applies for this premium service.',
      treatments: [
        'In-home psychiatric evaluations',
        'Medication management at home',
        'Geriatric home visits',
        'Post-hospitalization follow-up',
        'Mobility-limited patient care',
        'Caregiver consultation and education',
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Page Header */}
      <section className="pt-32 pb-16 bg-beige-light">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <motion.h1
            className="text-5xl md:text-6xl font-bold text-neutral-gray mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            Our Clinical Services
          </motion.h1>
          <motion.p
            className="text-xl text-neutral-gray/70 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            Comprehensive mental health care tailored to your unique needs
          </motion.p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={index}
                className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-lg transition-all duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <div
                  className="p-6 cursor-pointer"
                  onClick={() => setExpandedService(expandedService === index ? null : index)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4 flex-1">
                      <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                        <i className={`${service.icon} text-3xl text-primary`}></i>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-neutral-gray mb-2">
                          {service.title}
                        </h3>
                        <p className="text-neutral-gray/70 text-sm">
                          {service.shortDesc}
                        </p>
                      </div>
                    </div>
                    <button className="ml-4 w-8 h-8 flex items-center justify-center flex-shrink-0">
                      <i
                        className={`ri-${
                          expandedService === index ? 'subtract' : 'add'
                        }-line text-2xl text-primary transition-transform`}
                      ></i>
                    </button>
                  </div>
                </div>

                <AnimatePresence>
                  {expandedService === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-6 border-t border-gray-100 pt-6">
                        <p className="text-neutral-gray/80 mb-4 leading-relaxed">
                          {service.fullDesc}
                        </p>
                        <div className="space-y-2 mb-6">
                          {service.treatments.map((treatment, idx) => (
                            <div key={idx} className="flex items-start space-x-2">
                              <i className="ri-checkbox-circle-line text-primary mt-1 flex-shrink-0"></i>
                              <span className="text-sm text-neutral-gray/70">{treatment}</span>
                            </div>
                          ))}
                        </div>
                        <Link
                          to="/appointment"
                          className="inline-flex items-center space-x-2 bg-primary hover:bg-primary-dark text-white px-6 py-2.5 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap"
                        >
                          <span>Schedule This Service</span>
                          <i className="ri-arrow-right-line"></i>
                        </Link>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>

          {/* Bottom CTA */}
          <motion.div
            className="mt-16 bg-beige-light rounded-2xl p-12 text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h3 className="text-3xl font-bold text-neutral-gray mb-4">
              Not sure which service you need?
            </h3>
            <p className="text-lg text-neutral-gray/70 mb-6 max-w-2xl mx-auto">
              Contact us for a consultation. Our team will help you determine the best treatment approach for your unique situation.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/appointment"
                className="inline-flex items-center space-x-2 bg-primary hover:bg-primary-dark text-white px-8 py-3 rounded-full font-semibold transition-all cursor-pointer whitespace-nowrap"
              >
                <span>Schedule a Consultation</span>
                <i className="ri-arrow-right-line"></i>
              </Link>
              <a
                href="/payment-setup"
                className="px-8 py-3 bg-white text-[rgba(75,162,75)] border-2 border-[rgba(75,162,75)] rounded-md hover:bg-[rgba(75,162,75)] hover:text-white transition-colors font-medium whitespace-nowrap inline-flex items-center justify-center gap-2"
              >
                <i className="ri-secure-payment-line"></i>
                Set Up Payment
              </a>
              <a
                href="tel:+17206552560"
                className="inline-flex items-center space-x-2 border-2 border-primary text-primary hover:bg-primary hover:text-white px-8 py-3 rounded-full font-semibold transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line"></i>
                <span>(720) 655-2560</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;